import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Search, Plus, Pencil, Trash2, Loader2, RefreshCw, X, ImageIcon, MapPin } from "lucide-react";
import { projectsService, type Project } from "../../../lib/services/projectsService";
import type { Models } from "appwrite";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";

type ProjectDoc = Project & Models.Document;

const CATEGORIES = [
    "HVAC",
    "Heat Pump",
    "Cold Room",
    "Electrical",
    "Refrigerant ISO Tank",
    "Industrial HVAC"
];

const CATEGORY_COLORS: Record<string, string> = {
    "HVAC": "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
    "Heat Pump": "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-800",
    "Cold Room": "bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-400 dark:border-cyan-800",
    "Electrical": "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800",
    "Refrigerant ISO Tank": "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800",
    "Industrial HVAC": "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800",
};

const CATEGORIES_FILTER = ["All", ...CATEGORIES];

export function AdminProjects() {
    const navigate = useNavigate();
    const [projects, setProjects] = useState<ProjectDoc[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("All");
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const load = () => {
        setLoading(true);
        projectsService.list()
            .then((data) => setProjects(data as ProjectDoc[]))
            .catch(console.error)
            .finally(() => setLoading(false));
    };
    useEffect(load, []);

    const handleDelete = async (id: string) => {
        if (!confirm("Delete this project? This cannot be undone.")) return;
        setDeletingId(id);
        setProjects((p) => p.filter((x) => x.$id !== id));
        try { await projectsService.remove(id); }
        catch { load(); }
        finally { setDeletingId(null); }
    };

    const filtered = projects.filter((p) => {
        const matchSearch =
            p.title.toLowerCase().includes(search.toLowerCase()) ||
            p.location.toLowerCase().includes(search.toLowerCase()) ||
            p.category.toLowerCase().includes(search.toLowerCase());
        const matchCat = category === "All" || p.category === category;
        return matchSearch && matchCat;
    });

    return (
        <div className="w-full min-w-0 space-y-4">
            {/* ── Header row ── */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border">
                <div className="flex items-center justify-between w-full sm:w-auto">
                    <div className="flex items-center gap-3">
                        <h2 className="text-base font-semibold text-foreground">Projects</h2>
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{projects.length}</span>
                    </div>
                    <div className="sm:hidden flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={load} className="h-8 w-8 p-0 text-muted-foreground hover:text-white">
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                        </Button>
                        <Button onClick={() => navigate("/admin/projects/new")} size="sm" className="h-8 gap-1.5 px-3">
                            <Plus className="w-3.5 h-3.5" /> <span className="hidden xs:inline">Add</span>
                        </Button>
                    </div>
                </div>

                <div className="flex items-center gap-2 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0 hide-scrollbar">
                    {/* Search */}
                    <div className="relative flex-1 sm:flex-initial min-w-[140px] sm:w-[220px]">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                        <Input
                            placeholder="Search projects…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-8 h-8 text-sm bg-background shadow-none focus-visible:ring-1 border border-border w-full"
                        />
                        {search && (
                            <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>

                    {/* Category filter */}
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="h-8 rounded-md border border-border bg-background px-2.5 pr-7 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-ring appearance-none cursor-pointer shrink-0"
                        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center" }}
                    >
                        {CATEGORIES_FILTER.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>

                    {/* Actions (Desktop) */}
                    <div className="hidden sm:flex items-center gap-2 shrink-0">
                        <Button variant="ghost" size="sm" onClick={load} className="h-8 text-muted-foreground hover:text-white gap-1.5">
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                        </Button>
                        <Button onClick={() => navigate("/admin/projects/new")} size="sm" className="h-8 gap-1.5">
                            <Plus className="w-3.5 h-3.5" /> Add Project
                        </Button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center py-24 bg-card rounded border border-border min-h-[40vh]">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                    <p className="text-sm font-medium text-muted-foreground">Loading projects...</p>
                </div>
            ) : (
                <>
                    {/* Mobile cards */}
                    <div className="md:hidden space-y-4">
                        {filtered.map((p) => (
                            <Card key={p.$id} className="overflow-hidden shadow-none">
                                {p.imageUrl && (
                                    <div className="h-32 overflow-hidden border-b border-border">
                                        <img src={p.imageUrl} alt={p.title} className="w-full h-full object-cover" />
                                    </div>
                                )}
                                <CardContent className="p-4">
                                    <div className="flex items-start gap-4">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-semibold text-foreground truncate">{p.title}</p>
                                            <div className="flex flex-wrap items-center gap-2 mt-2">
                                                <Badge variant="outline" className={`border-transparent ${CATEGORY_COLORS[p.category] ?? "bg-muted text-muted-foreground"}`}>
                                                    {p.category}
                                                </Badge>
                                            </div>
                                            <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                                                <MapPin className="w-3 h-3" /> {p.location}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between mt-4">
                                        <div />
                                        <Badge variant="outline" className={p.status === "Completed" ? "bg-emerald-50 text-emerald-700 border-transparent" : "bg-amber-50 text-amber-700 border-transparent"}>
                                            {p.status}
                                        </Badge>
                                    </div>
                                    <div className="flex gap-2 mt-6 pt-4 border-t border-border/50">
                                        <Button variant="outline" size="sm" onClick={() => navigate(`/admin/projects/${p.$id}`)} className="flex-1 shadow-none">
                                            <Pencil className="w-3.5 h-3.5 mr-2" /> Edit
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={() => handleDelete(p.$id!)} disabled={deletingId === p.$id} className="flex-1 shadow-none text-destructive hover:text-destructive hover:bg-destructive/10">
                                            {deletingId === p.$id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <Trash2 className="w-3.5 h-3.5 mr-2" />} Delete
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                        {filtered.length === 0 && (
                            <div className="text-center py-16 bg-card rounded border border-border">
                                <p className="text-sm text-muted-foreground mb-3">No projects found.</p>
                                <Button variant="link" onClick={() => navigate("/admin/projects/new")} className="text-primary">Add your first project →</Button>
                            </div>
                        )}
                    </div>

                    {/* Desktop table */}
                    <div className="hidden md:block border border-border rounded overflow-hidden">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-muted/50 hover:bg-muted/50">
                                    <TableHead className="w-[80px] font-semibold">Image</TableHead>
                                    <TableHead className="font-semibold">Project Title</TableHead>
                                    <TableHead className="w-[180px] font-semibold">Category</TableHead>
                                    <TableHead className="w-[160px] font-semibold">Location</TableHead>
                                    <TableHead className="w-[120px] font-semibold">Status</TableHead>
                                    <TableHead className="w-[100px] text-right font-semibold">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map((p) => (
                                    <TableRow key={p.$id} className="group cursor-pointer" onClick={() => navigate(`/admin/projects/${p.$id}`)}>
                                        <TableCell>
                                            {p.imageUrl ? (
                                                <img src={p.imageUrl} alt={p.title} className="w-12 h-12 rounded object-cover border border-border" />
                                            ) : (
                                                <div className="w-12 h-12 rounded bg-muted flex items-center justify-center border border-border">
                                                    <ImageIcon className="w-5 h-5 text-muted-foreground/50" />
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell><span className="font-medium text-foreground">{p.title}</span></TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={`border-transparent font-normal ${CATEGORY_COLORS[p.category] ?? "bg-muted text-muted-foreground"}`}>
                                                {p.category}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                                <MapPin className="w-3.5 h-3.5" />
                                                <span className="truncate max-w-[120px]" title={p.location}>{p.location}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className={`font-normal ${p.status === "Completed" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                                                {p.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                                            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/projects/${p.$id}`)} className="h-8 w-8 text-muted-foreground hover:text-white">
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => handleDelete(p.$id!)} disabled={deletingId === p.$id} className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
                                                    {deletingId === p.$id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filtered.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} className="h-32 text-center">
                                            <div className="flex flex-col items-center gap-2">
                                                <p className="text-sm text-muted-foreground">No projects found.</p>
                                                <Button variant="link" onClick={() => navigate("/admin/projects/new")} className="text-primary">Add your first project →</Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </>
            )}
        </div>
    );
}
